import React from 'react';
import * as Mui from '@mui/material';
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Agent } from "model/agent";
import { updateAgent, resetError } from "redux/slice/agents"
import { RootState, getPayload } from "redux/store";
import { SaveButton } from "component/shared/save-button";
import { validate } from "shared/yup";
import * as yup from "yup";
import { styles } from "./style";
import * as env from "shared/env";

interface Props extends OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	}

interface OwnProps {
	agent: Agent;
	onClose: () => void;
}

interface State {
	firstName: string;
	lastName: string;
	title: string;
	email: string;
	homePhone: string;
	officePhone: string;
	cellPhone: string;
	officeAddress: string;
	officeCity: string;
	officeState: string;
	officeZip: string;
	submitted: boolean;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		loading: state.agents.loading,
		resetError,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => {
	return bindActionCreators(
		{
			updateAgent,
		},
		dispatch
	);
};

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			firstName: this.props.agent.firstName || "",
			lastName: this.props.agent.lastName || "",
			title: this.props.agent.designation || "",
			email: this.props.agent.email || "",
			homePhone: this.props.agent.homePhone || "",
			officePhone: this.props.agent.officePhone || "",
			cellPhone: this.props.agent.cellPhone || "",
			officeAddress: this.props.agent.officeAddress || "",
			officeCity: this.props.agent.officeCity || "",
			officeState: this.props.agent.officeState || "",
			officeZip: this.props.agent.officeZip || "",
			submitted: false,
		};
	}

	private async save() {
		const { updateAgent, agent, onClose } = this.props;
		const { cellPhone, email, homePhone, officePhone, title, lastName, firstName, officeAddress, officeCity, officeState, officeZip } = this.state;
		const errors = validate(this.validationSchema, this.state);
		if (!errors) {
			const agentResource = getPayload( await updateAgent( { agent: {
				...agent,
				cellPhone,
				email,
				homePhone,
				firstName,
				lastName,
				officePhone,
				designation: title,
				officeAddress,
				officeCity,
				officeState,
				officeZip
			}}));
			if (agentResource) {
				onClose();
			}
		}
	}

	private validationSchema = {
		designation: yup
			.string()
			.test(
				"designation-max-length",
				"Designation must be less than 50 characters.",
				(designation) => !designation || designation.length <= 50
			),
		email: yup.string().email("Invalid email.").required("Email is required."),
	};

	render() {
		const { firstName, lastName, title, email, homePhone, officePhone, cellPhone, officeAddress, officeCity, officeState, officeZip, submitted } = this.state;
		const { loading, classes = {} } = this.props;
		const errors = validate(this.validationSchema, this.state);

		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Grid container spacing={2}>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>First Name</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="first"
								onChange={(event) => this.setState({firstName: event.target.value})}
								value={firstName}
								variant="outlined"
							/>
						</Mui.Grid>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>Last Name</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="last"
								onChange={(event) => this.setState({lastName: event.target.value})}
								value={lastName}
								variant="outlined"
							/>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Typography className={classes.fieldLabel}>Title / Designation</Mui.Typography>
					<Mui.TextField
						fullWidth
						name="title"
						onChange={(event) => this.setState({title: event.target.value})}
						value={title}
						variant="outlined"
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Typography className={classes.fieldLabel}>Email</Mui.Typography>
					<Mui.TextField
						fullWidth
						error={submitted && !!errors && !!errors.email}
						helperText={submitted && errors && errors.email}
						name="email"
						onChange={(event) => this.setState({email: event.target.value})}
						value={email}
						variant="outlined"
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Typography className={classes.fieldLabel}>Home Phone</Mui.Typography>
					<Mui.TextField
						fullWidth
						name="homePhone"
						onChange={(event) => this.setState({homePhone: event.target.value})}
						value={homePhone}
						variant="outlined"
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Typography className={classes.fieldLabel}>Office Phone</Mui.Typography>
					<Mui.TextField
						fullWidth
						name="officePhone"
						onChange={(event) => this.setState({officePhone: event.target.value})}
						value={officePhone}
						variant="outlined"
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Typography className={classes.fieldLabel}>Cell Phone</Mui.Typography>
					<Mui.TextField
						fullWidth
						name="cellPhone"
						onChange={(event) => this.setState({cellPhone: event.target.value})}
						value={cellPhone}
						variant="outlined"
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Grid container spacing={2}>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>Address</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="address"
								onChange={(event) => this.setState({officeAddress: event.target.value})}
								value={officeAddress}
								variant="outlined"
							/>
						</Mui.Grid>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>City</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="city"
								onChange={(event) => this.setState({officeCity: event.target.value})}
								value={officeCity}
								variant="outlined"
							/>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Grid container spacing={2}>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>State</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="state"
								onChange={(event) => this.setState({officeState: event.target.value})}
								value={officeState}
								variant="outlined"
							/>
						</Mui.Grid>
						<Mui.Grid item xs={6}>
							<Mui.Typography className={classes.fieldLabel}>Zip</Mui.Typography>
							<Mui.TextField
								fullWidth
								name="zip"
								onChange={(event) => this.setState({officeZip: event.target.value})}
								value={officeZip}
								variant="outlined"
							/>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Grid container spacing={1} justifyContent="flex-end">
						<Mui.Grid item>
							<Mui.Button
								variant="outlined"
								color="primary"
								onClick={() => this.props.onClose()}
							>
								Cancel
							</Mui.Button>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button
								variant="contained"
								color="primary"
								onClick={() => {
									this.setState({ submitted: true });
									this.save();
								}}
							>
								Save
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>
		);
	}
}

export const PersonalInfo = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
